import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import type {
  CatalogueItem,
  Participant,
  ParticipantAnswer,
  Seat,
  SharedNote,
} from "../types";
import { MAX_PERSONAL_CONTEXT_LENGTH } from "../types";
import {
  answerIsComplete,
  createDraftAnswer,
  normalizeParticipantAnswer,
} from "../lib/session";
import {
  ResponseEditor,
  ResponseReadOnly,
  ResponseSummary,
} from "./ResponseControls";
import { BookmarkIcon } from "./Icons";
import { MobilePanelToggle } from "./MobilePanelToggle";

interface ItemWorkspaceProps {
  roomId: string;
  mode: "local" | "remote";
  item: CatalogueItem;
  ownSeat: Seat;
  ownParticipant: Participant;
  partnerParticipant: Participant;
  ownAnswer: ParticipantAnswer | null;
  partnerAnswer: ParticipantAnswer | null;
  partnerSaved: boolean;
  note: SharedNote | null;
  onSaveAnswer: (answer: ParticipantAnswer) => Promise<void>;
  onSaveNote: (note: SharedNote) => Promise<void>;
  onAdvance: () => void;
  onRegisterBeforeNavigate: (
    handler: (() => Promise<void>) | null,
  ) => void;
  onHandoff: (() => void) | null;
  onDeleteCustom: ((item: CatalogueItem) => void) | null;
}

type FocusedField = "note" | "context" | null;
type MobilePanel =
  | "note"
  | "partner-response";

export function ItemWorkspace({
  roomId,
  mode,
  item,
  ownSeat,
  ownParticipant,
  partnerParticipant,
  ownAnswer,
  partnerAnswer,
  partnerSaved,
  note,
  onSaveAnswer,
  onSaveNote,
  onAdvance,
  onRegisterBeforeNavigate,
  onHandoff,
  onDeleteCustom,
}: ItemWorkspaceProps) {
  const draftKey = `feel-scene:draft:${roomId}:${ownSeat}:${item.id}`;
  const [draft, setDraft] = useState<ParticipantAnswer>(() =>
    readDraft(
      draftKey,
      createDraftAnswer(item, ownParticipant.stance, ownAnswer ?? undefined),
      item,
      ownParticipant.stance,
    ),
  );
  const [noteDraft, setNoteDraft] = useState(note?.text ?? "");
  const [noteBaseVersion, setNoteBaseVersion] = useState(note?.version ?? 0);
  const [rowImportant, setRowImportant] = useState(Boolean(note?.important));
  const [focused, setFocused] = useState<FocusedField>(null);
  const [contextOpen, setContextOpen] = useState(Boolean(draft.context));
  const [mobilePanels, setMobilePanels] = useState<Set<MobilePanel>>(
    () => new Set(),
  );
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const dirty = useRef(false);
  const draftRef = useRef(draft);
  const noteDraftRef = useRef(noteDraft);
  const noteBaseVersionRef = useRef(noteBaseVersion);
  const rowImportantRef = useRef(rowImportant);
  const savedNoteRef = useRef({
    text: note?.text ?? "",
    important: Boolean(note?.important),
  });
  const answerSavedRef = useRef(Boolean(ownAnswer?.savedAt));
  const answerSaveRef = useRef<Promise<void> | null>(null);
  const pendingSaveRef = useRef<() => Promise<void>>(async () => undefined);
  const ownResponseRef = useRef<HTMLElement>(null);
  const contextPointerScrollRef = useRef<number | null>(null);
  const contextRef = useRef<HTMLTextAreaElement>(null);
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const mobileSaveRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const initial = readDraft(
      draftKey,
      createDraftAnswer(item, ownParticipant.stance, ownAnswer ?? undefined),
      item,
      ownParticipant.stance,
    );
    setDraft(initial);
    setNoteDraft(note?.text ?? "");
    setNoteBaseVersion(note?.version ?? 0);
    setRowImportant(Boolean(note?.important));
    setContextOpen(Boolean(initial.context));
    setMobilePanels(new Set());
    setFocused(null);
    setMessage("");
    dirty.current = false;
    draftRef.current = initial;
    noteDraftRef.current = note?.text ?? "";
    noteBaseVersionRef.current = note?.version ?? 0;
    rowImportantRef.current = Boolean(note?.important);
    savedNoteRef.current = {
      text: note?.text ?? "",
      important: Boolean(note?.important),
    };
    answerSavedRef.current = Boolean(ownAnswer?.savedAt);
  }, [draftKey]);

  useEffect(() => {
    if (!dirty.current && ownAnswer) {
      answerSavedRef.current = Boolean(ownAnswer.savedAt);
      draftRef.current = ownAnswer;
      setDraft(ownAnswer);
    }
  }, [ownAnswer?.version]);

  useEffect(() => {
    if (note && focused !== "note") {
      setNoteDraft(note.text);
      setNoteBaseVersion(note.version);
      setRowImportant(Boolean(note.important));
      noteDraftRef.current = note.text;
      noteBaseVersionRef.current = note.version;
      rowImportantRef.current = Boolean(note.important);
      savedNoteRef.current = {
        text: note.text,
        important: Boolean(note.important),
      };
    }
  }, [note?.version]);

  const revealed = Boolean(ownAnswer?.savedAt && partnerAnswer?.savedAt);
  const editingText = focused !== null;
  const noteChanged =
    noteDraft !== savedNoteRef.current.text ||
    rowImportant !== savedNoteRef.current.important;

  useEffect(() => {
    if (!revealed) return;
    setMobilePanels((current) => {
      if (current.has("partner-response")) return current;
      const next = new Set(current);
      next.add("partner-response");
      return next;
    });
  }, [item.id, revealed]);

  const updateDraft = (next: ParticipantAnswer) => {
    dirty.current = true;
    draftRef.current = next;
    setDraft(next);
    sessionStorage.setItem(draftKey, JSON.stringify(next));
  };

  const updateNoteDraft = (text: string) => {
    noteDraftRef.current = text;
    setNoteDraft(text);
  };

  const saveSharedNote = async (important = rowImportantRef.current) => {
    const text = noteDraftRef.current;
    if (
      text === savedNoteRef.current.text &&
      important === savedNoteRef.current.important
    ) {
      return;
    }
    const version = noteBaseVersionRef.current;
    const next: SharedNote = {
      itemId: item.id,
      text,
      important,
      updatedAt: Date.now(),
      version,
    };
    await onSaveNote(next);
    noteBaseVersionRef.current = version + 1;
    savedNoteRef.current = { text, important };
    setNoteBaseVersion(version + 1);
  };

  const saveAnswerDraft = async (force = false): Promise<void> => {
    if ((!dirty.current && !force) || !answerIsComplete(draftRef.current)) return;
    if (answerSaveRef.current) {
      await answerSaveRef.current;
      if (dirty.current || (force && !answerSavedRef.current)) {
        await saveAnswerDraft(force);
      }
      return;
    }
    const snapshot = draftRef.current;
    const request = onSaveAnswer(snapshot);
    answerSaveRef.current = request;
    try {
      await request;
      answerSavedRef.current = true;
      const current = draftRef.current;
      const saved = {
        ...current,
        savedAt: Date.now(),
        version: Math.max(current.version, snapshot.version + 1),
      };
      draftRef.current = saved;
      setDraft(saved);
      if (current === snapshot) {
        dirty.current = false;
        sessionStorage.removeItem(draftKey);
      }
    } finally {
      answerSaveRef.current = null;
    }
  };

  const savePendingChanges = async () => {
    setSaving(true);
    try {
      await saveSharedNote();
      await saveAnswerDraft();
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "Changes could not be saved. Your draft is preserved.",
      );
    } finally {
      setSaving(false);
    }
  };

  pendingSaveRef.current = savePendingChanges;

  useEffect(() => {
    const handler = () => pendingSaveRef.current();
    onRegisterBeforeNavigate(handler);
    return () => onRegisterBeforeNavigate(null);
  }, [onRegisterBeforeNavigate]);

  const saveNoteOnly = async () => {
    if (!noteChanged) {
      setMessage("Shared note is already saved.");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      await saveSharedNote();
      setMessage("Shared note saved.");
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "The shared note could not be saved. Your draft is preserved.",
      );
    } finally {
      setSaving(false);
    }
  };

  const completeItem = async (advanceAfterSave = false) => {
    if (!answerIsComplete(draftRef.current)) {
      setMessage("Choose interest and experience for each active perspective.");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      await saveSharedNote();
      await saveAnswerDraft(!answerSavedRef.current);
      if (advanceAfterSave) {
        setMessage("");
        onAdvance();
        return;
      }
      if (partnerSaved) {
        setMessage("");
      } else if (mode === "local" && onHandoff) {
        setMessage(`Saved. Pass to ${partnerParticipant.name}.`);
        onHandoff();
      } else {
        setMessage("Saved. Waiting for your partner.");
        onAdvance();
      }
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "This item could not be saved. Your draft is preserved.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleTextShortcut = (
    event: KeyboardEvent<HTMLTextAreaElement>,
    field: FocusedField,
  ) => {
    if (event.key === "Enter" && event.shiftKey) {
      event.preventDefault();
      if (field === "context") {
        setFocused("note");
        focusForFlow(noteRef.current);
      } else if (field === "note" && !answerIsComplete(draftRef.current)) {
        void saveNoteOnly();
      } else if (field === "note") {
        void completeItem(true);
      } else {
        void completeItem();
      }
    }
  };

  const toggleContext = () => {
    if (contextOpen) {
      setContextOpen(false);
      setFocused(null);
      return;
    }
    setContextOpen(true);
    setFocused("context");
    requestAnimationFrame(() => {
      focusForFlow(contextRef.current);
    });
  };

  const keepContextStableOnBlur = () => {
    const owner = ownResponseRef.current;
    requestAnimationFrame(() => {
      if (owner?.contains(document.activeElement)) return;
      setFocused(null);
    });
  };

  const moveAfterResponse = () => {
    void saveAnswerDraft();
    if (window.matchMedia("(max-width: 760px)").matches) {
      setFocused(null);
      requestAnimationFrame(() => focusForFlow(mobileSaveRef.current));
      return;
    }
    setFocused("context");
    focusForFlow(contextRef.current);
  };

  const toggleMobilePanel = (
    panel: MobilePanel,
    focusTarget: HTMLElement | null = null,
  ) => {
    const opening = !mobilePanels.has(panel);
    setMobilePanels((current) => {
      const next = new Set(current);
      if (next.has(panel)) next.delete(panel);
      else next.add(panel);
      return next;
    });
    if (opening && focusTarget) {
      requestAnimationFrame(() => focusForFlow(focusTarget));
    }
  };

  const toggleRowImportant = async () => {
    const next = !rowImportantRef.current;
    rowImportantRef.current = next;
    setRowImportant(next);
    setSaving(true);
    try {
      await saveSharedNote(next);
    } catch (error) {
      rowImportantRef.current = !next;
      setRowImportant(!next);
      setMessage(
        error instanceof Error
          ? error.message
          : "The bookmark could not be saved.",
      );
    } finally {
      setSaving(false);
    }
  };

  const partnerState = useMemo(() => {
    if (revealed) return "revealed";
    if (partnerSaved) return "saved";
    return "waiting";
  }, [revealed, partnerSaved]);

  useEffect(() => {
    if (revealed) {
      setMessage("");
    } else if (ownAnswer?.savedAt) {
      setMessage("Saved. Waiting for your partner.");
    }
  }, [revealed, ownAnswer?.savedAt]);

  return (
    <article
      className={`item-workspace glass-panel seat-${ownSeat.toLocaleLowerCase()} ${editingText ? "text-focused" : ""} focus-${focused ?? "none"} ${rowImportant ? "row-important" : ""}`}
      aria-labelledby="current-item-title"
    >
      <button
        className="row-bookmark"
        type="button"
        aria-label={rowImportant ? "Remove row bookmark" : "Bookmark this row"}
        aria-pressed={rowImportant}
        onClick={() => void toggleRowImportant()}
      >
        <BookmarkIcon />
      </button>
      <section className="item-reference">
        {item.custom ? <span className="custom-label">Custom term</span> : null}
        <h1 id="current-item-title">{item.term}</h1>
        <p>{item.definition}</p>
        <p className="save-status" role="status" aria-live="polite">
          {message}
        </p>
        <button
          className="button primary save-button"
          type="button"
          onClick={() => void completeItem()}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save"}
        </button>
        {item.custom && !item.locked && onDeleteCustom ? (
          <button
            className="text-button danger"
            type="button"
            onClick={() => onDeleteCustom(item)}
          >
            Delete custom term
          </button>
        ) : null}
      </section>

      <section
        className="own-response"
        ref={ownResponseRef}
        aria-label={`${ownParticipant.name}'s response`}
      >
        <ResponseEditor
          answer={draft}
          onChange={updateDraft}
          onComplete={moveAfterResponse}
          onNavigateHorizontal={(direction) => {
            if (direction > 0) moveAfterResponse();
          }}
        />
        <div className="context-controls">
          <button
            className="text-button accent"
            type="button"
            aria-expanded={contextOpen}
            onClick={toggleContext}
          >
            Context
          </button>
        </div>
        <div
          className={`context-editor ${contextOpen ? "" : "context-collapsed"}`}
        >
            <span className="column-label context-label">Context</span>
            <textarea
              ref={contextRef}
              data-flow-control
              aria-label="Personal context"
              value={draft.context}
              placeholder="When or how does this work for you, or how do you define this?"
              maxLength={MAX_PERSONAL_CONTEXT_LENGTH}
              onFocus={() => {
                setFocused("context");
              }}
              onBlur={keepContextStableOnBlur}
              onKeyDown={(event) => handleTextShortcut(event, "context")}
              onChange={(event) =>
                updateDraft({ ...draft, context: event.target.value })
              }
            />
            <button
              className="important-toggle context-important-toggle"
              type="button"
              role="checkbox"
              aria-checked={draft.important}
              onPointerDown={() => {
                contextPointerScrollRef.current =
                  ownResponseRef.current?.closest<HTMLElement>(
                    ".catalogue-stream",
                  )?.scrollTop ?? null;
              }}
              onClick={() => {
                const stream = ownResponseRef.current?.closest<HTMLElement>(
                  ".catalogue-stream",
                );
                updateDraft({
                  ...draftRef.current,
                  important: !draftRef.current.important,
                });
                requestAnimationFrame(() => {
                  const preservedScroll = contextPointerScrollRef.current;
                  if (stream && preservedScroll !== null) {
                    stream.scrollTop = preservedScroll;
                  } else if (stream) {
                    centreActiveRow(stream);
                  }
                  contextPointerScrollRef.current = null;
                  contextRef.current?.focus({ preventScroll: true });
                  if (stream && preservedScroll !== null) {
                    requestAnimationFrame(() => {
                      stream.scrollTop = preservedScroll;
                    });
                  }
                });
              }}
            >
              <span className="checkbox-mark" aria-hidden="true" />
              <span>This context is important</span>
            </button>
            {focused === "context" ? <TextShortcut action="notes" /> : null}
        </div>
      </section>

      <section
        className={`shared-note-column mobile-collapsible ${
          mobilePanels.has("note") ? "mobile-expanded" : ""
        }`}
      >
        <MobilePanelToggle
          label="Shared notes"
          expanded={mobilePanels.has("note")}
          controls="shared-note-panel"
          onToggle={() => toggleMobilePanel("note", noteRef.current)}
        />
        <label id="shared-note-panel" className="mobile-panel-content">
          <span className="column-label">Shared notes</span>
          <textarea
            ref={noteRef}
            data-flow-control
            aria-label="Shared notes"
            value={noteDraft}
            maxLength={2_000}
            placeholder="Questions or words you agree on"
            onFocus={() => {
              if (focused !== "note") setNoteBaseVersion(note?.version ?? 0);
              setFocused("note");
            }}
            onBlur={() => setFocused(null)}
            onKeyDown={(event) => handleTextShortcut(event, "note")}
            onChange={(event) => updateNoteDraft(event.target.value)}
          />
          {focused === "note" ? (
            <TextShortcut
              action={answerIsComplete(draft) ? "finish" : "note"}
            />
          ) : null}
        </label>
      </section>

      <PartnerResponse
        participant={partnerParticipant}
        answer={partnerAnswer}
        state={partnerState}
        expanded={mobilePanels.has("partner-response")}
        onToggle={() => toggleMobilePanel("partner-response")}
      />

      <button
        className="button primary mobile-save-button"
        type="button"
        ref={mobileSaveRef}
        onClick={() => void completeItem(true)}
        disabled={saving}
      >
        {saving ? "Saving…" : "Next"}
      </button>

    </article>
  );
}

function TextShortcut({
  action,
}: {
  action: "notes" | "finish" | "note";
}) {
  const instruction =
    action === "notes"
      ? "Shift + Enter for shared notes"
      : action === "finish"
        ? "Shift + Enter to finish this item"
        : action === "note"
          ? "Shift + Enter to save this note"
        : "Shift + Enter to save this note";
  return (
    <small className="text-shortcut">
      {instruction} · Enter for a new line
    </small>
  );
}

function PartnerResponse({
  participant,
  answer,
  state,
  expanded,
  onToggle,
}: {
  participant: Participant;
  answer: ParticipantAnswer | null;
  state: "revealed" | "saved" | "waiting";
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <section
      className={`partner-response partner-column mobile-collapsible ${
        expanded ? "mobile-expanded" : ""
      }`}
    >
      <MobilePanelToggle
        label={`${participant.name}’s response`}
        status={partnerPanelStatus(state)}
        expanded={expanded}
        controls="partner-response-panel"
        onToggle={onToggle}
      />
      <div id="partner-response-panel" className="mobile-panel-content">
        <span className="column-label">{participant.name}’s response</span>
        {state === "revealed" && answer ? (
          <>
            <div className="desktop-partner-response-controls">
              <ResponseReadOnly answer={answer} />
            </div>
            <div className="mobile-partner-response-summary">
              <ResponseSummary answer={answer} compact />
            </div>
            {answer.context ? (
              <aside className={answer.important ? "partner-context important" : "partner-context"}>
                {answer.important ? <strong>Important</strong> : null}
                <p>{answer.context}</p>
              </aside>
            ) : null}
            {answer.changedAfterReveal ? (
              <small className="changed-label">Changed since reveal</small>
            ) : null}
          </>
        ) : (
          <LockedState
            state={state === "saved" ? "saved" : "waiting"}
            savedLabel={`${participant.name} saved`}
          />
        )}
      </div>
    </section>
  );
}

function partnerPanelStatus(state: "revealed" | "saved" | "waiting") {
  if (state === "revealed") return "Revealed";
  if (state === "saved") return "Saved";
  return "Hidden";
}

function LockedState({
  state,
  savedLabel,
}: {
  state: "saved" | "waiting";
  savedLabel?: string;
}) {
  return (
    <div className="locked-state">
      {state === "saved" && savedLabel ? <strong>{savedLabel}</strong> : null}
      <span className="lock-shape" aria-hidden="true" />
      <p>Hidden until both save</p>
    </div>
  );
}

function readDraft(
  key: string,
  fallback: ParticipantAnswer,
  item: CatalogueItem,
  stance: Participant["stance"],
) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as ParticipantAnswer;
    return parsed.itemId === fallback.itemId
      ? normalizeParticipantAnswer(parsed, item, stance)
      : fallback;
  } catch {
    return fallback;
  }
}

function focusForFlow(element: HTMLElement | null) {
  if (!element) return;
  element.focus({ preventScroll: true });
  if (window.innerWidth <= 760) {
    element.scrollIntoView({
      block: "center",
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
    });
  }
}

function centreActiveRow(container: HTMLElement) {
  if (!window.matchMedia("(min-width: 1101px)").matches) return;
  const active = container.querySelector<HTMLElement>("[data-active-item]");
  if (!active) return;
  const containerBounds = container.getBoundingClientRect();
  const activeBounds = active.getBoundingClientRect();
  const header = active
    .closest(".catalogue-category")
    ?.querySelector<HTMLElement>(".catalogue-category-toggle");
  const headerHeight = header?.offsetHeight ?? 0;
  const availableHeight = container.clientHeight - headerHeight;
  if (activeBounds.height > availableHeight) return;
  const desiredTop =
    headerHeight + (availableHeight - activeBounds.height) / 2;
  container.scrollTop = Math.max(
    0,
    container.scrollTop +
      activeBounds.top -
      containerBounds.top -
      desiredTop,
  );
}
